import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
    HiHeart, 
    HiBeaker, 
    HiScissors, 
    HiLightningBolt, 
    HiShieldCheck,
    HiCamera,
    HiCog,
    HiChevronLeft,
    HiChevronRight
} from 'react-icons/hi'

const categoryIcons = {
    All: HiHeart,
    Dental: HiScissors,
    'Dialysis Consumables': HiHeart,
    Laboratory: HiBeaker,
    'General Medical Consumables': HiShieldCheck,
    'X-Ray Imaging': HiCamera,
    'Biomedical Engineering': HiCog,
}

const categoryColors = {
    All: 'bg-gradient-to-br from-medical-blue-400 to-medical-blue-600 text-white shadow-lg shadow-medical-blue-200',
    Dental: 'bg-gradient-to-br from-medical-teal-400 to-medical-teal-600 text-white shadow-lg shadow-medical-teal-200',
    'Dialysis Consumables': 'bg-gradient-to-br from-red-400 to-pink-600 text-white shadow-lg shadow-red-200',
    Laboratory: 'bg-gradient-to-br from-medical-green-400 to-medical-green-600 text-white shadow-lg shadow-medical-green-200',
    'General Medical Consumables': 'bg-gradient-to-br from-purple-400 to-purple-600 text-white shadow-lg shadow-purple-200',
    'X-Ray Imaging': 'bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-lg shadow-blue-200',
    'Biomedical Engineering': 'bg-gradient-to-br from-gray-400 to-gray-600 text-white shadow-lg shadow-gray-200',
}

const categoryBorderColors = {
    All: 'border-medical-blue-300',
    Dental: 'border-medical-teal-300',
    'Dialysis Consumables': 'border-red-300',
    Laboratory: 'border-medical-green-300',
    'General Medical Consumables': 'border-purple-300',
    'X-Ray Imaging': 'border-blue-300',
    'Biomedical Engineering': 'border-gray-300',
}

export default function CategoryShowcase({ categories, onCategorySelect, activeCategory }) {
    const scrollContainerRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)

    const checkScrollability = () => {
        const container = scrollContainerRef.current
        if (container) {
            setCanScrollLeft(container.scrollLeft > 0)
            setCanScrollRight(
                container.scrollLeft < container.scrollWidth - container.clientWidth - 1
            )
        }
    }

    useEffect(() => {
        const container = scrollContainerRef.current
        if (container) {
            checkScrollability()
            container.addEventListener('scroll', checkScrollability)
            window.addEventListener('resize', checkScrollability)
            return () => {
                container.removeEventListener('scroll', checkScrollability)
                window.removeEventListener('resize', checkScrollability)
            }
        }
    }, [])

    const scrollLeft = () => {
        const container = scrollContainerRef.current
        if (container) {
            container.scrollBy({ left: -300, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        const container = scrollContainerRef.current
        if (container) {
            container.scrollBy({ left: 300, behavior: 'smooth' })
        }
    }

    return (
        <div className="mb-12">
            <div className="relative">
                {/* Left Arrow Button */}
                {canScrollLeft && (
                    <motion.button
                        onClick={scrollLeft}
                        whileTap={{ scale: 0.9 }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg border border-gray-200 hover:bg-white hover:shadow-xl transition-all duration-200"
                        aria-label="Scroll left"
                    >
                        <HiChevronLeft className="w-6 h-6 text-medical-blue-600" />
                    </motion.button>
                )}

                {/* Right Arrow Button */}
                {canScrollRight && (
                    <motion.button
                        onClick={scrollRight}
                        whileTap={{ scale: 0.9 }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg border border-gray-200 hover:bg-white hover:shadow-xl transition-all duration-200"
                        aria-label="Scroll right"
                    >
                        <HiChevronRight className="w-6 h-6 text-medical-blue-600" />
                    </motion.button>
                )}

                {/* Left fade gradient */}
                <div className="absolute left-0 top-0 bottom-4 w-16 md:w-20 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none z-10"></div>
                {/* Right fade gradient */}
                <div className="absolute right-0 top-0 bottom-4 w-16 md:w-20 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-10"></div>
                
                {/* Horizontal scroll container */}
                <div 
                    ref={scrollContainerRef}
                    className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide pb-4 pt-4 pl-8 pr-8 md:pl-12 md:pr-12 scroll-smooth"
                    onScroll={checkScrollability}
                >
                    {categories.map((category, index) => {
                        const Icon = categoryIcons[category.name] || HiCog
                        const colorClass = categoryColors[category.name] || 'bg-gradient-to-br from-gray-400 to-gray-600 text-white shadow-lg'
                        const borderColor = categoryBorderColors[category.name] || 'border-gray-300'
                        const isActive = activeCategory === category.id

                        return (
                            <motion.button
                                key={category.id}
                                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: index * 0.05, type: 'spring' }}
                                onClick={() => onCategorySelect(category.id)}
                                whileHover={{ scale: 1.05, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                                className={`p-6 rounded-2xl transition-all duration-300 text-center group relative overflow-hidden flex-shrink-0 ${
                                    isActive
                                        ? `border-3 border-medical-blue-500 bg-gradient-to-br from-medical-blue-50 to-medical-teal-50 ring-4 ring-medical-blue-200/50 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05),0_0_0_1px_rgba(59,130,246,0.2),0_0_15px_rgba(2,132,199,0.2)]`
                                        : `border-3 border-gray-300 bg-white hover:border-medical-blue-400 hover:bg-gradient-to-br hover:from-gray-50 hover:to-medical-blue-50/30 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05),0_0_0_1px_rgba(59,130,246,0.15),0_0_10px_rgba(2,132,199,0.2)]`
                                }`}
                                style={{ borderWidth: '3px', minWidth: '140px' }}
                            >
                                {/* Background gradient effect */}
                                <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-300 ${
                                    category.name === 'All' ? 'from-medical-blue-400 to-medical-blue-600' :
                                    category.name === 'Dental' ? 'from-medical-teal-400 to-medical-teal-600' :
                                    category.name === 'Dialysis Consumables' ? 'from-red-400 to-pink-600' :
                                    category.name === 'Laboratory' ? 'from-medical-green-400 to-medical-green-600' :
                                    category.name === 'General Medical Consumables' ? 'from-purple-400 to-purple-600' :
                                    category.name === 'X-Ray Imaging' ? 'from-blue-400 to-blue-600' :
                                    'from-gray-400 to-gray-600'
                                }`}></div>
                                
                                {/* New Badge */}
                                {category.name === 'Dialysis Consumables' && (
                                    <div className="absolute top-2 right-2 z-20 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                                        NEW
                                    </div>
                                )}
                                
                                <div className="relative z-10">
                                    <div className={`w-14 h-14 ${colorClass} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                                        <Icon className="w-7 h-7" />
                                    </div>
                                    <span className={`text-sm font-bold block transition-colors ${
                                        isActive 
                                            ? 'text-medical-blue-700' 
                                            : 'text-gray-700 group-hover:text-medical-blue-600'
                                    }`}>
                                        {category.name}
                                    </span>
                                </div>
                            </motion.button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

